import { ReactNode } from "react";

interface ReportPanelProps {
  title: string;
  subtitle?: string;
  icon?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
  noPadding?: boolean;
}

export const ReportPanel = ({
  title,
  subtitle,
  icon,
  actions,
  children,
  className = "",
  noPadding = false,
}: ReportPanelProps) => {
  return (
    <div className={`bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between p-5 border-b border-gray-100">
        <div className="flex items-center gap-3">
          {icon && (
            <div className="w-9 h-9 bg-gradient-to-br from-emerald-100 to-teal-100 rounded-lg flex items-center justify-center">
              <div className="text-emerald-600">{icon}</div>
            </div>
          )}
          <div>
            <h3 className="text-base font-semibold text-gray-900">{title}</h3>
            {subtitle && <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>}
          </div>
        </div>
        {actions && <div className="flex items-center gap-2">{actions}</div>}
      </div>

      {/* Content */}
      <div className={noPadding ? "" : "p-5"}>{children}</div>
    </div>
  );
};

// Simple Card variant
export const SimpleCard = ({
  children,
  className = "",
}: {
  children: ReactNode;
  className?: string;
}) => (
  <div className={`bg-white rounded-2xl shadow-sm border border-gray-100 p-5 ${className}`}>
    {children}
  </div>
);

// Loading Skeleton for panels
export const PanelSkeleton = ({ rows = 3 }: { rows?: number }) => (
  <div className="animate-pulse space-y-4">
    {Array.from({ length: rows }).map((_, i) => (
      <div key={i} className="flex items-center gap-4">
        <div className="w-10 h-10 bg-gray-200 rounded-lg" />
        <div className="flex-1 space-y-2">
          <div className="h-4 bg-gray-200 rounded w-3/4" />
          <div className="h-3 bg-gray-200 rounded w-1/2" />
        </div>
        <div className="h-6 bg-gray-200 rounded w-16" />
      </div>
    ))}
  </div>
);

// Tabs for panel
interface TabItem {
  key: string;
  label: string;
  icon?: ReactNode;
}

interface PanelTabsProps {
  tabs: TabItem[];
  activeTab: string;
  onChange: (key: string) => void;
}

export const PanelTabs = ({ tabs, activeTab, onChange }: PanelTabsProps) => (
  <div className="flex items-center gap-1 bg-gray-100 p-1 rounded-lg">
    {tabs.map((tab) => (
      <button
        key={tab.key}
        type="button"
        onClick={() => onChange(tab.key)}
        className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
          activeTab === tab.key
            ? "bg-white text-emerald-700 shadow-sm"
            : "text-gray-600 hover:text-gray-900"
        }`}
      >
        {tab.icon}
        {tab.label}
      </button>
    ))}
  </div>
);

export default ReportPanel;
